import { DayPickerProps, PropsSingle } from 'react-day-picker';
import { VariantProps, cva } from 'class-variance-authority';

import { PopoverContent, PopoverTrigger, Popover } from '../popover/popover';
import { Calendar } from '../calendar/calendar';
import { DateFormatted } from '../date/date';
import { cn } from '../../utils';

export const datePickerVariants = cva(
  `flex items-center h-14 px-3 w-full peer rounded-md outline outline-border-default bg-background
    sm:text-sm text-sm
    disabled:text-muted-foreground disabled:outline-border-disabled disabled:cursor-not-allowed disabled:bg-body disabled:outline-none disabled:text-content-disabled
    focus:outline-surface focus:outline-1 focus:ring-3 focus:ring-border-sky focus:ring-offset-0
    hover:outline-border-hover
  `,
  {
    variants: {
      inputSize: {
        small: 'h-10',
        medium: 'h-12',
        large: 'h-14 rounded-lg',
      },
      hasError: {
        true: 'outline-critical',
      },
      selected: {
        true: 'text-content',
        false: 'text-weak',
      },
      hasShadow: { true: 'shadow-sm disabled:shadow-none' },
    },
    defaultVariants: {
      inputSize: 'medium',
      hasShadow: false,
      selected: false,
    },
  },
);

export type DatePickerProps = Omit<Partial<DayPickerProps> & Partial<PropsSingle>, 'required'> & {
  placeholder?: string;
  required?: boolean;
} & VariantProps<typeof datePickerVariants>;

function DatePicker({
  placeholder,
  inputSize,
  hasShadow,
  className,
  required,
  hasError,
  disabled,
  ...rest
}: DatePickerProps) {
  const { selected, lang } = rest;

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          className={cn(
            datePickerVariants({ selected: !!selected, hasShadow, inputSize, hasError }),
            className,
          )}
          disabled={disabled === true}
        >
          {selected ? (
            <DateFormatted format="DATE_SHORT" date={selected} language={lang} />
          ) : (
            <span
              className={cn({
                "after:content-['*'] after:text-critical": required,
              })}
            >
              {placeholder}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0">
        <Calendar mode="single" {...rest} disabled={disabled} />
      </PopoverContent>
    </Popover>
  );
}

DatePicker.displayName = 'DatePicker';

export { DatePicker };
